/**
 * Model step: choose default / custom zone / nearest-analogue ML for ML scenarios.
 */
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { fieldsApi, FieldSummary } from '@/api/fields';
import { scenariosApi } from '@/api/scenarios';
import { TrainPolygonEditor } from '@/components/Scenarios/TrainPolygonEditor';
import type {
  AnalogueFieldScope,
  AnalogueLayerConfig,
  AnalogueLayerState,
  AnalogueMatchMode,
  MlMode,
} from '@/types/analogueMl';
import type { CustomMLModelResponse, CustomMLPreviewResponse } from '@/types/customMl';

function formatApiError(err: unknown, fallback: string): string {
  if (err == null) return fallback;
  if (typeof err === 'string') return err;
  const anyErr = err as {
    detail?: unknown;
    message?: unknown;
    response?: { data?: { detail?: unknown } };
  };
  const detail = anyErr.detail ?? anyErr.response?.data?.detail ?? anyErr.message;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg);
        }
        return JSON.stringify(item);
      })
      .join('; ');
  }
  if (detail != null) return JSON.stringify(detail);
  return fallback;
}

export interface ScenarioModelStepProps {
  scenarioId: string;
  readOnly?: boolean;
  coordinate?: string | null;
  onMlModeChange?: (mlMode: MlMode) => void;
  onModelReadyChange?: (ready: boolean) => void;
  onContinue?: () => void;
}

type AnalogueRow = AnalogueLayerState & {
  deviation_pct: number;
  match_mode: AnalogueMatchMode;
  field_scope: AnalogueFieldScope;
};

type JobProgress = {
  label: string;
  determinate: boolean;
  /** 0–100 when determinate */
  value: number;
};

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
}

function toAnalogueRow(r: AnalogueLayerState): AnalogueRow {
  return {
    ...r,
    deviation_pct: r.deviation_pct ?? 30,
    match_mode: (r.match_mode as AnalogueMatchMode) || 'mean',
    field_scope: (r.field_scope as AnalogueFieldScope) || 'both',
  };
}

export const ScenarioModelStep: React.FC<ScenarioModelStepProps> = ({
  scenarioId,
  readOnly = false,
  coordinate = null,
  onMlModeChange,
  onModelReadyChange,
  onContinue,
}) => {
  const [mlMode, setMlMode] = useState<MlMode>('default');
  const [busy, setBusy] = useState(false);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [customName, setCustomName] = useState('Custom zone model');
  const [polygon, setPolygon] = useState<string | null>(null);
  const [preview, setPreview] = useState<CustomMLPreviewResponse | null>(null);
  const [trainedModel, setTrainedModel] = useState<CustomMLModelResponse | null>(null);
  const [acceptedModel, setAcceptedModel] = useState<CustomMLModelResponse | null>(null);
  const [fields, setFields] = useState<FieldSummary[]>([]);

  const [analogueRows, setAnalogueRows] = useState<AnalogueRow[]>([]);
  const [analogueReady, setAnalogueReady] = useState(false);

  const configsFromRows = useCallback(
    (rows: AnalogueRow[]): AnalogueLayerConfig[] =>
      rows.map((r) => ({
        scenario_layer_id: r.scenario_layer_id,
        deviation_pct: Number(r.deviation_pct) || 30,
        match_mode: (r.match_mode as AnalogueMatchMode) || 'mean',
        field_scope: (r.field_scope as AnalogueFieldScope) || 'both',
      })),
    [],
  );

  const modelReady = useMemo(() => {
    if (mlMode === 'default') return true;
    if (mlMode === 'custom') return !!acceptedModel && acceptedModel.status === 'accepted';
    if (mlMode === 'analogue') return analogueReady && analogueRows.every((r) => r.status === 'ready');
    return false;
  }, [mlMode, acceptedModel, analogueReady, analogueRows]);

  useEffect(() => {
    onModelReadyChange?.(modelReady);
  }, [modelReady, onModelReadyChange]);

  useEffect(() => {
    if (!busy) {
      setElapsedSec(0);
      return;
    }
    const started = Date.now();
    setElapsedSec(0);
    const id = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [busy]);

  const startJob = useCallback((label: string, determinate = false, value = 0) => {
    setBusy(true);
    setJobProgress({ label, determinate, value });
  }, []);

  const updateJob = useCallback((label: string, value?: number) => {
    setJobProgress((prev) =>
      prev
        ? {
            ...prev,
            label,
            value: value != null ? value : prev.value,
            determinate: value != null ? true : prev.determinate,
          }
        : { label, determinate: value != null, value: value ?? 0 },
    );
  }, []);

  const endJob = useCallback(() => {
    setBusy(false);
    setJobProgress(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fieldsApi.getFieldsList();
        if (!cancelled) setFields(list.fields);
      } catch {
        // optional map context
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadState = useCallback(async () => {
    const state = await scenariosApi.getModelState(scenarioId);
    const mode = (['default', 'custom', 'analogue'].includes(state.ml_mode)
      ? state.ml_mode
      : 'default') as MlMode;
    setMlMode(mode);
    onMlModeChange?.(mode);
    if (state.custom_model) {
      setTrainedModel(state.custom_model);
      setAcceptedModel(state.custom_model.status === 'accepted' ? state.custom_model : null);
      setCustomName(state.custom_model.name);
      setPolygon(state.custom_model.train_polygon);
    } else {
      setTrainedModel(null);
      setAcceptedModel(null);
    }
    setAnalogueRows(
      (state.analogue_layers || []).map((r) => ({
        ...r,
        deviation_pct: r.deviation_pct ?? 30,
        match_mode: (r.match_mode as AnalogueMatchMode) || 'mean',
        field_scope: (r.field_scope as AnalogueFieldScope) || 'both',
      })),
    );
    setAnalogueReady(!!state.analogue_ready);
  }, [scenarioId, onMlModeChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setBusy(true);
        setError(null);
        await loadState();
      } catch (err) {
        if (!cancelled) setError(formatApiError(err, 'Failed to load model state'));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadState]);

  const handleModeChange = async (next: MlMode) => {
    if (readOnly) return;
    setError(null);
    setMessage(null);
    setMlMode(next);
    onMlModeChange?.(next);
    setBusy(true);
    try {
      await scenariosApi.setModelMode(scenarioId, next);
      if (next === 'default') {
        setAcceptedModel(null);
        setMessage(
          'Default model selected. Reservoir configuration will use the global training pipeline.',
        );
      }
      await loadState();
    } catch (err) {
      setError(formatApiError(err, 'Failed to update ML mode'));
    } finally {
      setBusy(false);
    }
  };

  const handlePreviewCustom = async () => {
    setError(null);
    setMessage(null);
    if (!polygon) {
      setError('Draw a training polygon first');
      return;
    }
    setBusy(true);
    try {
      const res = await scenariosApi.previewCustomMl(scenarioId, polygon);
      setPreview(res);
      setMessage(
        res.n_oil_layers === 0
          ? `Preview: 0 eligible oil reservoirs in ${res.n_fields} field(s).`
          : `Preview: ${res.n_oil_layers} oil reservoirs in ${res.n_fields} fields `
            + `(${res.n_wc_layers} with water-cut coefficients).`,
      );
    } catch (err) {
      setError(formatApiError(err, 'Preview failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleTrainCustom = async () => {
    setError(null);
    setMessage(null);
    if (!polygon) {
      setError('Draw a training polygon first');
      return;
    }
    startJob('Starting custom zone training...', true, 0);
    try {
      const model = await scenariosApi.trainCustomMl(
        scenarioId,
        {
          name: customName.trim() || 'Custom zone model',
          train_polygon: polygon,
          existing_model_id: trainedModel?.id,
        },
        (p) => updateJob(p.message, p.progress),
      );
      setTrainedModel(model);
      setMessage('Training finished. Review metrics, then Accept to bind the model.');
    } catch (err) {
      setError(formatApiError(err, 'Training failed'));
    } finally {
      endJob();
    }
  };

  const handleAcceptCustom = async () => {
    if (!trainedModel) return;
    setBusy(true);
    setError(null);
    try {
      const model = await scenariosApi.acceptCustomMl(scenarioId, trainedModel.id);
      setAcceptedModel(model);
      setTrainedModel(model);
      setMlMode('custom');
      onMlModeChange?.('custom');
      setMessage('Custom model accepted. You can continue to Reservoir configuration.');
    } catch (err) {
      setError(formatApiError(err, 'Accept failed'));
    } finally {
      setBusy(false);
    }
  };

  const updateAnalogueRow = (layerId: string, patch: Partial<AnalogueRow>) => {
    setAnalogueRows((prev) =>
      prev.map((r) => (r.scenario_layer_id === layerId ? { ...r, ...patch, status: 'draft' } : r)),
    );
    setAnalogueReady(false);
  };

  const handleRefreshMatches = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await scenariosApi.previewAnalogueMl(scenarioId, configsFromRows(analogueRows));
      setAnalogueRows((prev) =>
        prev.map((r) => {
          const hit = res.layers.find((x) => x.scenario_layer_id === r.scenario_layer_id);
          return hit ? { ...r, n_matches: hit.n_matches } : r;
        }),
      );
      setMessage('Match counts refreshed.');
    } catch (err) {
      setError(formatApiError(err, 'Refresh matches failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleApplyAnalogue = async () => {
    startJob('Starting analogue apply...', true, 0);
    setError(null);
    setMessage(null);
    try {
      const res = await scenariosApi.applyAnalogueMl(
        scenarioId,
        configsFromRows(analogueRows),
        (p) => updateJob(p.message, p.progress),
      );
      setAnalogueRows(
        res.layers.map((r) => ({
          ...r,
          deviation_pct: r.deviation_pct ?? 30,
          match_mode: (r.match_mode as AnalogueMatchMode) || 'mean',
          field_scope: (r.field_scope as AnalogueFieldScope) || 'both',
        })),
      );
      setAnalogueReady(res.layers.every((r) => r.status === 'ready'));
      setMlMode('analogue');
      onMlModeChange?.('analogue');
      setMessage('Analogue models trained and saved for all layers.');
    } catch (err) {
      setError(formatApiError(err, 'Apply failed'));
    } finally {
      endJob();
    }
  };

  const handleLooTest = async () => {
    startJob('Starting Blind test...', true, 0);
    setError(null);
    setMessage(null);
    try {
      const res = await scenariosApi.looTestAnalogueMl(
        scenarioId,
        configsFromRows(analogueRows),
        (p) => updateJob(p.message, p.progress),
      );
      setAnalogueRows((prev) =>
        prev.map((r) => {
          const hit = res.layers.find((x) => x.scenario_layer_id === r.scenario_layer_id);
          if (!hit) return r;
          return {
            ...r,
            n_matches: hit.n_matches,
            loo_summary: hit.loo
              ? {
                  oil_peak_norm_median_pct: hit.loo.oil?.median ?? null,
                  wc_mae_median_pct:
                    hit.loo.wc?.mae_median_pct
                    ?? (hit.loo.wc as { mae_median_pp?: number | null } | undefined)?.mae_median_pp
                    ?? null,
                }
              : r.loo_summary,
          };
        }),
      );
      const failed = res.layers.filter((l) => l.error);
      setMessage(
        failed.length
          ? `Blind test finished with ${failed.length} layer error(s).`
          : 'Blind test finished.',
      );
    } catch (err) {
      setError(formatApiError(err, 'Blind test failed'));
    } finally {
      endJob();
    }
  };

  if (readOnly) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Model
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mode: {mlMode}
          {mlMode === 'default' && ' — default model for traditional terrigenous continental oil fields'}
          {mlMode === 'custom' && acceptedModel && ` — ${acceptedModel.name}`}
          {mlMode === 'analogue' && ` — ${analogueRows.filter((r) => r.status === 'ready').length}/${analogueRows.length} layers ready`}
        </Typography>
        {mlMode === 'analogue' && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reservoir</TableCell>
                <TableCell>Deviation %</TableCell>
                <TableCell>Match</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Matches</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analogueRows.map((r) => (
                <TableRow key={r.scenario_layer_id}>
                  <TableCell>{r.layer_name}</TableCell>
                  <TableCell>{r.deviation_pct}</TableCell>
                  <TableCell>{r.match_mode}</TableCell>
                  <TableCell>{r.field_scope}</TableCell>
                  <TableCell>{r.n_matches}</TableCell>
                  <TableCell>{r.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Model
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose how type-well curves are predicted for this scenario. Configure here before Reservoir configuration.
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <RadioGroup
          value={mlMode}
          onChange={(e) => handleModeChange(e.target.value as MlMode)}
        >
          <FormControlLabel
            value="default"
            control={<Radio />}
            label="Default model — for traditional terrigenous continental oil fields"
          />
          <FormControlLabel
            value="custom"
            control={<Radio />}
            label="Custom model by location"
          />
          <FormControlLabel
            value="analogue"
            control={<Radio />}
            label="Nearest analogues"
          />
        </RadioGroup>
      </FormControl>

      {mlMode === 'default' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Uses the global ExtraTrees training set on Reservoir configuration and Production results. No
          extra configuration needed.
        </Alert>
      )}

      {mlMode === 'custom' && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Custom model name"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            disabled={busy}
          />
          <TrainPolygonEditor
            polygon={polygon}
            onChange={setPolygon}
            flyToCoordinate={coordinate}
            readOnly={false}
            mapHeight={420}
            enabled
            fields={fields}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={handlePreviewCustom} disabled={busy || !polygon}>
              Preview train set
            </Button>
            <Button variant="contained" onClick={handleTrainCustom} disabled={busy || !polygon}>
              {busy ? 'Working…' : 'Train'}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleAcceptCustom}
              disabled={busy || !trainedModel || trainedModel.status === 'draft'}
            >
              Accept
            </Button>
          </Stack>
          {preview && (
            <Alert severity="info">
              Fields in zone: {preview.n_fields}. Oil reservoirs: {preview.n_oil_layers}.
              Water-cut reservoirs: {preview.n_wc_layers}. Features: {preview.feature_attribute_ids.length}.
            </Alert>
          )}
          {acceptedModel && (
            <Alert severity="success">Bound model: {acceptedModel.name} (accepted).</Alert>
          )}
        </Stack>
      )}

      {mlMode === 'analogue' && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {analogueRows.length === 0 ? (
            <Alert severity="warning">Add reservoirs first, then return to configure analogues.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reservoir</TableCell>
                  <TableCell width={110}>Deviation %</TableCell>
                  <TableCell width={120}>Match mode</TableCell>
                  <TableCell width={140}>Field scope</TableCell>
                  <TableCell width={90}>Matches</TableCell>
                  <TableCell width={90}>Status</TableCell>
                  <TableCell>Blind test (oil / WC)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analogueRows.map((r) => (
                  <TableRow key={r.scenario_layer_id}>
                    <TableCell>{r.layer_name}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.deviation_pct}
                        onChange={(e) =>
                          updateAnalogueRow(r.scenario_layer_id, {
                            deviation_pct: Number(e.target.value),
                          })
                        }
                        inputProps={{ min: 1, max: 200, step: 1 }}
                        disabled={busy}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={r.match_mode}
                          onChange={(e) =>
                            updateAnalogueRow(r.scenario_layer_id, {
                              match_mode: e.target.value as AnalogueMatchMode,
                            })
                          }
                          disabled={busy}
                        >
                          <MenuItem value="mean">Mean</MenuItem>
                          <MenuItem value="max">Max</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Scope</InputLabel>
                        <Select
                          label="Scope"
                          value={r.field_scope}
                          onChange={(e) =>
                            updateAnalogueRow(r.scenario_layer_id, {
                              field_scope: e.target.value as AnalogueFieldScope,
                            })
                          }
                          disabled={busy}
                        >
                          <MenuItem value="both">Both</MenuItem>
                          <MenuItem value="continental">Continental</MenuItem>
                          <MenuItem value="offshore">Offshore</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>{r.n_matches}</TableCell>
                    <TableCell>{r.status}</TableCell>
                    <TableCell>
                      {r.loo_summary?.oil_peak_norm_median_pct != null
                        ? `${Number(r.loo_summary.oil_peak_norm_median_pct).toFixed(1)}%`
                        : '—'}
                      {' / '}
                      {r.loo_summary?.wc_mae_median_pct != null
                        ? `${Number(r.loo_summary.wc_mae_median_pct).toFixed(1)}%`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              onClick={handleRefreshMatches}
              disabled={busy || analogueRows.length === 0}
            >
              Refresh match counts
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyAnalogue}
              disabled={busy || analogueRows.length === 0}
            >
              Apply (train & save)
            </Button>
            <Button
              variant="outlined"
              onClick={handleLooTest}
              disabled={busy || analogueRows.length === 0}
            >
              Run Blind test
            </Button>
          </Stack>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {jobProgress && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {jobProgress.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatElapsed(elapsedSec)}
              {jobProgress.determinate ? ` · ${Math.round(jobProgress.value)}%` : ''}
            </Typography>
          </Stack>
          <LinearProgress
            variant={jobProgress.determinate ? 'determinate' : 'indeterminate'}
            value={jobProgress.determinate ? jobProgress.value : undefined}
          />
        </Box>
      )}

      {onContinue && (
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={busy || !modelReady}
          sx={{ mt: 1, minWidth: 200 }}
        >
          Continue to Reservoir configuration
        </Button>
      )}
    </Box>
  );
};
